import { useEffect, useRef } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { GeoSearchResult } from 'lib/api/getGeoSearch';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

import { QuickQuoteInputs } from '../../types';
import { mapGeoResultsToOptions, useGeoSearch } from '../hooks/useGeoSearch';

/**
 * Injects a synthetic result so `RHFDebounceSelect` can resolve a display
 * label for AI-prefilled values that aren't yet in the search results.
 */
function augmentDataForField(
  data: Maybe<GeoSearchResult[]>,
  fieldValue: unknown
): Maybe<GeoSearchResult[]> {
  const base = data ?? [];

  if (!fieldValue || typeof fieldValue !== 'string' || !fieldValue.trim()) {
    return base;
  }

  if (base.some((r) => r.id === fieldValue)) {
    return base;
  }

  return [
    {
      id: fieldValue,
      value: fieldValue,
      type: '',
      source: '',
      displayType: '',
    } as GeoSearchResult,
    ...base,
  ];
}

function shouldFetchPrefilledLocation(location: unknown): location is string {
  if (typeof location !== 'string') {
    return false;
  }

  const trimmed = location.trim();
  if (!trimmed) {
    return false;
  }

  // Geo IDs should resolve via results; only search raw suggestion text.
  if (trimmed.startsWith('tloc:') || trimmed.startsWith('gplace:')) {
    return false;
  }

  return true;
}

interface StopsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}

/** Pickup/delivery location dropdowns (geo search) and date pickers */
export function StopsSection({ formMethods }: StopsSectionProps) {
  const {
    control,
    getValues,
    formState: { errors },
    setValue,
    trigger,
  } = formMethods;

  const pickup = useGeoSearch(setValue, 0);
  const delivery = useGeoSearch(setValue, 1);
  const pickupDate = useWatch({ control, name: 'pickupDate' });
  const pickupLocation = useWatch({ control, name: 'stops.0.location' });
  const deliveryLocation = useWatch({ control, name: 'stops.1.location' });
  const lastPrefetchedPickup = useRef('');
  const lastPrefetchedDelivery = useRef('');

  useEffect(() => {
    trigger('deliveryDate');
  }, [pickupDate, trigger]);

  useEffect(() => {
    if (!shouldFetchPrefilledLocation(pickupLocation)) {
      lastPrefetchedPickup.current = '';
      return;
    }

    const query = pickupLocation.trim();
    if (lastPrefetchedPickup.current === query) {
      return;
    }

    lastPrefetchedPickup.current = query;
    pickup.fetchWithQuery(query);
  }, [pickup.fetchWithQuery, pickupLocation]);

  useEffect(() => {
    if (!shouldFetchPrefilledLocation(deliveryLocation)) {
      lastPrefetchedDelivery.current = '';
      return;
    }
    const query = deliveryLocation.trim();
    if (lastPrefetchedDelivery.current === query) {
      return;
    }
    lastPrefetchedDelivery.current = query;
    delivery.fetchWithQuery(query);
  }, [delivery.fetchWithQuery, deliveryLocation]);

  const validatePickupDate = (value: MaybeUndef<Date>): true | string => {
    if (!value) {
      return 'Pickup date is required';
    }
    const selected = new Date(value);
    selected.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selected >= today || 'Pickup date must be today or later';
  };

  const validateDeliveryDate = (value: MaybeUndef<Date>): true | string => {
    const pickupVal = getValues('pickupDate');
    if (!value || !pickupVal) {
      return true;
    }
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    const p = new Date(pickupVal);
    p.setHours(0, 0, 0, 0);
    return d >= p || 'Delivery must be on or after pickup date';
  };

  const geoSelectProps = {
    isLoading: false,
    mapOptions: mapGeoResultsToOptions,
    callFieldOnChangeWithParent: true,
    required: true,
    placeholder: 'Search by name or address',
    showSearchParamDropdown: false,
    searchFieldDefault: 'nameAddress' as const,
    showRefreshButton: false,
    useSimpleOptionRender: true,
  };

  return (
    <Flex direction='col' gap='sm' className='w-full mx-0'>
      {/* Pickup */}
      <Grid cols='1' gap='xs' className='w-full mx-0'>
        <Typography variant='body' weight='semibold' className='mb-1'>
          Pickup
        </Typography>

        <Flex direction='row' gap='xs' className='w-full items-start'>
          <RHFDebounceSelect
            {...geoSelectProps}
            name='stops.0.location'
            label='Location'
            control={control}
            errors={errors}
            data={augmentDataForField(pickup.results, pickupLocation)}
            fetchOptions={pickup.fetchOptions}
            parentOnChange={pickup.handleSelect}
            className='flex-[3] min-w-0 max-w-none [&_label]:leading-4'
          />

          <Flex direction='col' className='flex-[2] min-w-0'>
            <Label
              name='pickupDate'
              required
              className='text-[13px] font-medium leading-4'
            >
              Date
            </Label>
            <Controller
              name='pickupDate'
              control={control}
              rules={{
                required: 'Pickup date is required',
                validate: validatePickupDate,
              }}
              render={({ field }) => (
                <DatePicker
                  field={field}
                  className='mt-1 pl-2'
                  calendarClassName='mr-1.5'
                />
              )}
            />
            <div className='mt-1 min-h-[18px]'>
              {errors.pickupDate?.message ? (
                <Typography variant='body-xs' className='text-error-500'>
                  {errors.pickupDate.message}
                </Typography>
              ) : (
                <Typography
                  variant='body-xs'
                  className='text-error-500 invisible'
                >
                  Pickup date is required
                </Typography>
              )}
            </div>
          </Flex>
        </Flex>
      </Grid>

      {/* Dropoff */}
      <Grid cols='1' gap='xs' className='w-full mx-0'>
        <Typography variant='body' weight='semibold' className='my-1'>
          Dropoff
        </Typography>

        <Flex direction='row' gap='xs' className='w-full items-start'>
          <RHFDebounceSelect
            {...geoSelectProps}
            name='stops.1.location'
            label='Location'
            control={control}
            errors={errors}
            data={augmentDataForField(delivery.results, deliveryLocation)}
            fetchOptions={delivery.fetchOptions}
            parentOnChange={delivery.handleSelect}
            className='flex-[3] min-w-0 max-w-none [&_label]:leading-4'
          />

          <Flex direction='col' className='flex-[2] min-w-0'>
            <Label
              name='deliveryDate'
              required={false}
              className='text-[13px] font-medium leading-4'
            >
              Date
              <Typography variant='body-xs' className='ml-1 leading-none'>
                (optional)
              </Typography>
            </Label>
            <Controller
              name='deliveryDate'
              control={control}
              rules={{ validate: validateDeliveryDate }}
              render={({ field }) => (
                <DatePicker
                  field={field}
                  className='mt-1 pl-2'
                  calendarClassName='mr-1.5'
                />
              )}
            />
            <div className='mt-1 min-h-[18px]'>
              {errors.deliveryDate?.message ? (
                <Typography variant='body-xs' className='text-error-500'>
                  {errors.deliveryDate.message}
                </Typography>
              ) : (
                <Typography
                  variant='body-xs'
                  className='text-error-500 invisible'
                >
                  Delivery must be on or after pickup date
                </Typography>
              )}
            </div>
          </Flex>
        </Flex>
      </Grid>
    </Flex>
  );
}
